import { QueryClient } from '@tanstack/react-query'
import { PRICES_KEY, ORDERS_SUMMARY_KEY, postPricesBulk, GETPricesResponse } from '@/feature/financial-model/ecommerce/revenues/api'
import { resetGlobalQueryKey, TOTALS_KEY } from '@/api/query-keys'
import { parsePeriod } from '@/utils/sidepanel'

const getPeriodMapKey = (year: number, month: number) => `${year}-${String(month).padStart(2, `0`)}`

export const updateCacheForPriceValue = (
  queryClient: QueryClient,
  financialModelId: string,
  payload: any,
  value: number,
  startFrom?: { year: number; month: number },
  exactPeriod?: { year: number; month: number }
) => {
  if (!financialModelId || !payload?.queryPeriod || !payload?.view || !payload?.customerSegmentId || !payload?.product || !payload?.mutateDataKey)
    return

  const queryKey = PRICES_KEY.concat([financialModelId, payload.queryPeriod, payload.view])

  queryClient.setQueryData<GETPricesResponse>(queryKey, current => {
    if (!current) return current

    return {
      ...current,
      data: current.data.map(yearData => {
        if (!yearData?.customer_segments) return yearData

        if (yearData.product_id !== payload.product?.productId) return yearData

        return {
          ...yearData,
          customer_segments: yearData.customer_segments.map(customerSegment => {
            if (customerSegment.customer_segment_id !== payload.customerSegmentId) return customerSegment

            return {
              ...customerSegment,
              prices: customerSegment.prices.map(price => {
                if (price.period_type !== `forecast`) return price

                const { year, month } = parsePeriod(price.period)

                if (exactPeriod) {
                  if (year !== exactPeriod.year || month !== exactPeriod.month) return price
                } else if (startFrom) {
                  const isGreaterOrEqual = year! > startFrom.year || (year === startFrom.year && month! >= startFrom.month)
                  if (!isGreaterOrEqual) return price
                }

                return {
                  ...price,
                  [payload.mutateDataKey!]: value
                }
              })
            }
          })
        }
      })
    }
  })
}

export const applyPriceValues = async ({
  payload,
  value,
  startFrom,
  exactPeriod,
  financialModelId,
  queryClient
}: {
  payload: any
  value: number
  startFrom?: { year: number; month: number }
  exactPeriod?: { year: number; month: number }
  financialModelId: string
  queryClient: QueryClient
}) => {
  const mutateDataKey = payload?.mutateDataKey
  const product = payload?.product
  const periodValuesByKey = payload?.periodValuesByKey as Record<string, number> | undefined

  if (!mutateDataKey || !product) return

  const forecastRows = product.prices.filter((price: any) => price.period_type === `forecast`)

  if (!forecastRows.length) return

  const bulkPayload = forecastRows.map((price: any) => {
    const { year, month } = parsePeriod(price.period)
    const periodKey = getPeriodMapKey(year ?? 0, month ?? 0)
    const mappedValue = periodValuesByKey?.[periodKey]

    let targetValue = price[mutateDataKey]

    if (mappedValue !== undefined) {
      targetValue = mappedValue
    } else if (exactPeriod) {
      if (year === exactPeriod.year && month === exactPeriod.month) targetValue = value
    } else if (startFrom) {
      const isGreaterOrEqual = year! > startFrom.year || (year === startFrom.year && month! >= startFrom.month)
      if (isGreaterOrEqual) targetValue = value
    } else {
      targetValue = value
    }

    const basePayload = {
      id: price.id,
      product_id: product.productId,
      customer_segment_id: payload.customerSegmentId ?? null,
      year: year ?? 0,
      month: month ?? 0,
      type: price.period_type,
      unit_price: price.unit_price ? Number(price.unit_price) : null,
      gross_price: price.gross_price ? Number(price.gross_price) : null,
      discount: price.discount ? Number(price.discount) : null,
      net_price: price.net_price ? Number(price.net_price) : null,
      config: price.config
    }

    return { ...basePayload, [mutateDataKey]: targetValue }
  })

  await postPricesBulk(financialModelId, bulkPayload as any[])

  if (!periodValuesByKey) {
    updateCacheForPriceValue(queryClient, financialModelId, payload, value, startFrom, exactPeriod)
  }

  queryClient.invalidateQueries({ queryKey: PRICES_KEY })
  queryClient.invalidateQueries({ queryKey: ORDERS_SUMMARY_KEY })

  resetGlobalQueryKey([TOTALS_KEY])
}
